/**
 * User Activity Service
 * Tracks user sessions and activities for compliance and audit purposes
 */

import { supabase } from '@/integrations/supabase/client';

export interface ActivityLogEntry {
  user_id?: string;
  organization_id?: string;
  clerk_user_id: string;
  clerk_session_id?: string;
  activity_type: 'login' | 'logout' | 'page_view' | 'api_call' | 'action' | 'session_refresh';
  activity_description?: string;
  activity_metadata?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  device_type?: 'desktop' | 'mobile' | 'tablet';
  browser?: string;
  os?: string;
  country_code?: string;
  region?: string;
  city?: string;
  session_started_at?: string;
  last_active_at?: string;
  response_time_ms?: number;
}

// Browser detection utilities
function detectBrowser(userAgent: string): string {
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  return 'Unknown';
}

function detectOS(userAgent: string): string {
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
  return 'Unknown';
}

function detectDeviceType(userAgent: string): 'desktop' | 'mobile' | 'tablet' {
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) return 'mobile';
  return 'desktop';
}

// Get client IP address (best effort, may not work behind proxies)
async function getClientIP(): Promise<string | null> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch {
    return null;
  }
}

// Get geolocation from IP (optional, requires API key for production)
async function getGeolocation(ip: string): Promise<{ country?: string; region?: string; city?: string } | null> {
  try {
    // For production, use a service like ipapi, ipgeolocation, etc.
    // Using HTTPS to avoid mixed content issues
    const response = await fetch(`https://ipapi.co/${ip}/json/`);
    const data = await response.json();
    if (data.country_code) {
      return {
        country: data.country_code,
        region: data.region,
        city: data.city
      };
    }
    return null;
  } catch {
    return null;
  }
}

export class UserActivityService {
  private static instance: UserActivityService;
  private sessionId: string | null = null;
  private userId: string | null = null;
  private organizationId: string | null = null;
  private lastActivityTime: number = Date.now();
  private activityCheckInterval: NodeJS.Timeout | null = null;
  private idleTimeoutMinutes: number = 30; // Default idle timeout
  
  private constructor() {}
  
  static getInstance(): UserActivityService {
    if (!UserActivityService.instance) {
      UserActivityService.instance = new UserActivityService();
    }
    return UserActivityService.instance;
  }
  
  // Initialize session tracking
  async initializeSession(
    clerkUserId: string, 
    clerkSessionId: string,
    userProfileId?: string,
    organizationId?: string,
    idleTimeoutMinutes?: number
  ) {
    this.sessionId = clerkSessionId;
    this.userId = userProfileId || null;
    this.organizationId = organizationId || null;
    
    if (idleTimeoutMinutes) {
      this.idleTimeoutMinutes = idleTimeoutMinutes;
    }
    
    // Log login activity
    await this.logActivity({
      clerk_user_id: clerkUserId,
      clerk_session_id: clerkSessionId,
      user_id: userProfileId,
      organization_id: organizationId,
      activity_type: 'login',
      activity_description: 'User logged in',
      session_started_at: new Date().toISOString()
    });
    
    // Start idle monitoring
    this.startIdleMonitoring();
  }
  
  // Log user activity
  async logActivity(entry: Partial<ActivityLogEntry>) {
    try {
      const userAgent = navigator.userAgent;
      const ip = await getClientIP();
      const geo = ip ? await getGeolocation(ip) : null;
      
      const fullEntry: ActivityLogEntry = {
        ...entry,
        user_agent: userAgent,
        browser: detectBrowser(userAgent),
        os: detectOS(userAgent),
        device_type: detectDeviceType(userAgent),
        ip_address: ip || undefined,
        country_code: geo?.country,
        region: geo?.region,
        city: geo?.city,
        last_active_at: new Date().toISOString()
      };
      
      // Use upsert for session-based activities to avoid duplicates
      if (entry.clerk_session_id && ['login', 'logout', 'session_refresh'].includes(entry.activity_type)) {
        const { error } = await supabase
          .from('user_activity_logs')
          .upsert(fullEntry, {
            onConflict: 'clerk_session_id,activity_type',
            ignoreDuplicates: false
          });
        
        if (error) {
          console.error('Error logging activity:', error);
        }
      } else {
        // For other activities, just insert
        const { error } = await supabase
          .from('user_activity_logs')
          .insert(fullEntry);
        
        if (error) {
          console.error('Error logging activity:', error);
        }
      }
      
      // Update last activity time
      this.lastActivityTime = Date.now();
    } catch (error) {
      console.error('Failed to log user activity:', error);
    }
  }
  
  // Track page navigation
  async trackPageView(page: string, metadata?: Record<string, any>) {
    if (!this.sessionId) return;
    
    await this.logActivity({
      clerk_user_id: localStorage.getItem('clerk_user_id') || '',
      clerk_session_id: this.sessionId,
      user_id: this.userId || undefined,
      organization_id: this.organizationId || undefined,
      activity_type: 'page_view',
      activity_description: `Viewed page: ${page}`,
      activity_metadata: metadata
    });
  }
  
  // Track user actions
  async trackAction(action: string, metadata?: Record<string, any>) {
    if (!this.sessionId) return;
    
    await this.logActivity({
      clerk_user_id: localStorage.getItem('clerk_user_id') || '',
      clerk_session_id: this.sessionId,
      user_id: this.userId || undefined,
      organization_id: this.organizationId || undefined,
      activity_type: 'action',
      activity_description: action,
      activity_metadata: metadata
    });
  }
  
  // Track API calls (for rate limiting)
  async trackApiCall(endpoint: string, method: string, responseTime: number, metadata?: Record<string, any>) {
    if (!this.sessionId) return;
    
    await this.logActivity({
      clerk_user_id: localStorage.getItem('clerk_user_id') || '',
      clerk_session_id: this.sessionId,
      user_id: this.userId || undefined,
      organization_id: this.organizationId || undefined,
      activity_type: 'api_call',
      activity_description: `${method} ${endpoint}`,
      activity_metadata: metadata,
      response_time_ms: Math.round(responseTime)
    });
  }
  
  // Monitor idle timeout
  private startIdleMonitoring() {
    // Clear existing interval if any
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
    }
    
    // Check every minute for idle timeout
    this.activityCheckInterval = setInterval(() => {
      const idleTime = (Date.now() - this.lastActivityTime) / 1000 / 60; // in minutes
      
      if (idleTime >= this.idleTimeoutMinutes) {
        this.handleIdleTimeout();
      }
    }, 60000); // Check every minute
    
    // Track user activity
    const updateActivity = () => {
      this.lastActivityTime = Date.now();
    };
    
    // Add event listeners for user activity
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);
  }
  
  // Handle idle timeout
  private async handleIdleTimeout() {
    console.log('User idle timeout reached');
    
    // Log the timeout
    await this.logActivity({
      clerk_user_id: localStorage.getItem('clerk_user_id') || '',
      clerk_session_id: this.sessionId || undefined,
      user_id: this.userId || undefined,
      organization_id: this.organizationId || undefined,
      activity_type: 'logout',
      activity_description: 'Auto-logout due to idle timeout'
    });
    
    // Trigger logout (this would need to be integrated with Clerk)
    window.dispatchEvent(new CustomEvent('idle-timeout'));
  }
  
  // Clean up session
  async endSession(reason: string = 'User logged out') {
    if (!this.sessionId) return;
    
    // Log logout activity
    await this.logActivity({
      clerk_user_id: localStorage.getItem('clerk_user_id') || '',
      clerk_session_id: this.sessionId,
      user_id: this.userId || undefined,
      organization_id: this.organizationId || undefined,
      activity_type: 'logout',
      activity_description: reason
    });
    
    // Clear interval
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
    
    // Reset session data
    this.sessionId = null;
    this.userId = null;
    this.organizationId = null;
  }
  
  // Get user activity summary
  async getUserActivitySummary(clerkUserId: string, days: number = 30) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const { data, error } = await supabase
      .from('user_activity_logs')
      .select('*')
      .eq('clerk_user_id', clerkUserId)
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching activity summary:', error);
      return null;
    }
    
    return data;
  }
  
  // Get organization activity summary (admin only)
  async getOrganizationActivitySummary(organizationId: string, days: number = 30) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const { data, error } = await supabase
      .from('user_activity_logs')
      .select('*')
      .eq('organization_id', organizationId)
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching organization activity:', error);
      return null;
    }
    
    return data;
  }
}

// Export singleton instance
export const userActivityService = UserActivityService.getInstance();